import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Zap, Fuel, Wifi, Wrench, Package, FileText, Paperclip } from "lucide-react";
import { Money } from "./Money";
import { ReceiptViewer } from "./ReceiptViewer";

const CATEGORY_ICON = {
  61: { icon: Zap,      color: "text-amber-500" },
  62: { icon: Package,  color: "text-emerald-500" },
  63: { icon: Wrench,   color: "text-sky-500" },
  64: { icon: Fuel,     color: "text-rose-500" },
  65: { icon: Wifi,     color: "text-violet-500" },
};
const STATUS_STYLE = {
  paid:              "bg-emerald-500/12 text-emerald-600 border-emerald-500/40",
  partial:           "bg-sky-500/12 text-sky-600 border-sky-500/40",
  unpaid:            "bg-amber-500/12 text-amber-700 border-amber-500/40",
  pending_approval:  "bg-yellow-500/15 text-yellow-700 border-yellow-500/40",
  awaiting_delivery: "bg-sky-500/12 text-sky-700 border-sky-500/40",
  rejected:          "bg-rose-500/12 text-rose-600 border-rose-500/40",
};

const Field = ({ label, value, mono = false }) => (
  <div className="grid min-w-0 gap-0.5">
    <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className={`truncate text-xs font-bold ${mono ? "font-mono" : ""}`} dir={mono ? "ltr" : undefined}>
      {value || "—"}
    </span>
  </div>
);

// بطاقة فاتورة مرنة — عنوان أعلى/قيمة أسفل. تعمل على الجوال والحاسب بلا كسر.
export const BillRow = ({ bill, branchName, supplierName, accountName }) => {
  const { t } = useTranslation();
  const [showAttach, setShowAttach] = useState(false);
  const catKey = (bill.expense_account || "").toString().slice(0, 2);
  const Cat = CATEGORY_ICON[catKey] || { icon: FileText, color: "text-muted-foreground" };
  const Icon = Cat.icon;

  return (
    <div className="rounded-2xl border border-border/50 bg-background/70 p-4 shadow-neu-sm transition hover:-translate-y-0.5 hover:border-primary/40"
         data-testid={`bill-row-${bill.id}`}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {bill.attachment_url ? (
            <button type="button" onClick={() => setShowAttach(true)} data-testid={`bill-attach-${bill.id}`}
                    className="grid h-9 w-9 shrink-0 place-content-center rounded-lg border border-primary/30 bg-primary/8 shadow-neu-sm hover:bg-primary/15">
              <Paperclip className="h-4 w-4 text-primary" />
            </button>
          ) : null}
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase text-muted-foreground">{t("acct.bills.col.invoice")}</p>
            <p className="truncate font-mono text-sm font-black text-primary" dir="ltr">
              {bill.supplier_invoice_no || t("acct.bills.noInvoiceNo")}
            </p>
          </div>
        </div>
        <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold ${STATUS_STYLE[bill.status] || ""}`}>
          {t(`acct.billStatus.${bill.status}`)}
        </span>
      </div>

      <div className="mb-3 flex items-center gap-2 rounded-xl bg-muted/40 px-3 py-2">
        <Icon className={`h-4 w-4 shrink-0 ${Cat.color}`} />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase text-muted-foreground">{t("acct.bills.col.party")}</p>
          <p className="truncate text-sm font-black">{supplierName || bill.description || "—"}</p>
        </div>
        <Money value={bill.total} currency={bill.currency} className="shrink-0 text-base font-black text-primary" />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label={t("acct.bills.col.category")}  value={accountName || bill.expense_account} />
        <Field label={t("acct.bills.col.branch")}    value={branchName} />
        <Field label={t("acct.bills.col.date")}      value={bill.date} mono />
        <Field label={t("acct.bills.col.delivered")} value={bill.created_by} />
        <Field label={t("acct.bills.col.act")}       value={bill.entry_number} mono />
      </div>

      {showAttach ? <ReceiptViewer url={bill.attachment_url} onClose={() => setShowAttach(false)} /> : null}
    </div>
  );
};
